import React from 'react';
import {
  X,
  Download,
  ExternalLink,
  FileText,
  Image,
  Video,
  Music,
  File,
} from 'lucide-react';
import { DocumentUrl } from '@/types/document-store.types';
import { downloadFile } from '@/utils/downloadFile';
import {
  getFileTypeInfo,
  getFileTypeDisplayName,
} from '@/utils/fileTypeIconUtils';
import toast from 'react-hot-toast';

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: DocumentUrl | null;
  /** Optional document store context — when provided, downloads triggered from
   * this modal are recorded in the audit trail with the parent store info. */
  storeId?: string;
  storeName?: string;
}

export default function DocumentPreviewModal({
  isOpen,
  onClose,
  document,
  storeId,
  storeName,
}: DocumentPreviewModalProps) {
  // No need for blob URL fetching anymore - S3 files don't have Content-Disposition: attachment

  if (!isOpen || !document) return null;

  const getFileCategory = (fileType: string): string => {
    const type = fileType?.toLowerCase();

    // Handle MIME types
    if (type.includes('/')) {
      if (
        type.includes('pdf') ||
        type.includes('word') ||
        type.includes('text')
      )
        return 'document';
      if (type.includes('image')) return 'image';
      if (type.includes('video')) return 'video';
      if (type.includes('audio')) return 'audio';
      if (
        type.includes('sheet') ||
        type.includes('excel') ||
        type.includes('csv')
      )
        return 'spreadsheet';
      return 'other';
    }

    // Handle extensions
    if (['pdf', 'txt', 'docx', 'doc'].includes(type)) return 'document';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(type))
      return 'image';
    if (['mp4', 'avi', 'mov', 'webm', 'mkv'].includes(type)) return 'video';
    if (['mp3', 'wav', 'm4a', 'ogg'].includes(type)) return 'audio';
    if (['csv', 'xlsx', 'xls'].includes(type)) return 'spreadsheet';
    return 'other';
  };

  const handleDownload = async () => {
    const toastId = toast.loading('Preparing download...');
    try {
      await downloadFile(document.key, document.name, {
        module: 'document_store',
        resource_id: document._id,
        resource_name: document.name,
        parent_id: storeId,
        parent_name: storeName,
      });
      toast.success('File downloaded successfully', { id: toastId });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file', { id: toastId });
    }
  };

  const handleOpenInNewTab = () => {
    // Open the direct S3 URL in a new tab
    console.log('RR', document);
    window.open(document.url, '_blank', 'noopener,noreferrer');
  };

  const renderPreview = () => {
    const category = getFileCategory(document.fileType);
    const url = document.url; // Use direct S3 URL for all file types

    switch (category) {
      case 'image':
        return (
          <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
            <img
              src={url}
              alt={document.name}
              className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
              onError={(e) => {
                e.currentTarget.src = '';
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML =
                  '<p class="text-gray-500">Failed to load image</p>';
              }}
            />
          </div>
        );

      case 'video':
        // Check if it's a MOV or AVI file (limited browser support)
        const isMOV =
          document.fileType === 'video/quicktime' ||
          document.fileType === 'mov' ||
          document.name.toLowerCase().endsWith('.mov');
        const isAVI =
          document.fileType === 'video/x-msvideo' ||
          document.fileType === 'avi' ||
          document.name.toLowerCase().endsWith('.avi');

        if (isMOV || isAVI) {
          // Show download message for unsupported video formats
          return (
            <div className="flex flex-col items-center justify-center p-12 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg">
              <Video className="w-16 h-16 text-purple-500 mb-4" />
              <p className="text-gray-700 font-medium mb-2">
                Video Preview Not Available
              </p>
              <p className="text-sm text-gray-600 mb-2">{document.name}</p>
              <p className="text-xs text-gray-500 mb-6 text-center max-w-md">
                {isMOV ? '.MOV' : '.AVI'} files have limited browser support.
                Please download to view.
              </p>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download to View
              </button>
            </div>
          );
        }

        // Standard video preview for supported formats (MP4, WEBM, etc.)
        return (
          <div className="flex items-center justify-center p-4 bg-black rounded-lg">
            <video
              controls
              className="max-w-full max-h-[60vh] rounded-lg"
              preload="metadata"
            >
              <source
                src={url}
                type={
                  document.fileType.includes('/')
                    ? document.fileType
                    : `video/${document.fileType}`
                }
              />
              Your browser does not support the video tag.
            </video>
          </div>
        );

      case 'audio':
        return (
          <div className="flex flex-col items-center justify-center p-8 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg">
            <Music className="w-16 h-16 text-purple-500 mb-4" />
            <p className="text-gray-700 font-medium mb-4">{document.name}</p>
            <audio controls className="w-full max-w-md">
              <source
                src={url}
                type={
                  document.fileType.includes('/')
                    ? document.fileType
                    : `audio/${document.fileType}`
                }
              />
              Your browser does not support the audio element.
            </audio>
          </div>
        );

      case 'document':
        if (
          document.fileType === 'pdf' ||
          document.fileType === 'application/pdf'
        ) {
          return (
            <div className="w-full h-[70vh] bg-gray-50 rounded-lg overflow-hidden">
              <object
                data={url}
                type="application/pdf"
                className="w-full h-full border-0"
                title={document.name}
              >
                <div className="flex flex-col items-center justify-center h-full p-12">
                  <FileText className="w-16 h-16 text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">
                    Unable to display PDF preview
                  </p>
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-2 px-4 py-2 bg-brand-indigo-600 text-white rounded-lg hover:bg-brand-indigo-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download to View
                  </button>
                </div>
              </object>
            </div>
          );
        } else if (
          document.fileType === 'txt' ||
          document.fileType === 'text/plain'
        ) {
          return (
            <div className="w-full h-[60vh] bg-gray-50 rounded-lg p-4 overflow-auto">
              <iframe
                src={url}
                className="w-full h-full border-0 bg-white rounded"
                title={document.name}
              />
            </div>
          );
        } else {
          return (
            <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg">
              <FileText className="w-16 h-16 text-gray-400 mb-4" />
              <p className="text-gray-600 mb-2">
                Document Preview Not Available
              </p>
              <p className="text-sm text-gray-500 mb-6">{document.name}</p>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-brand-indigo-600 text-white rounded-lg hover:bg-brand-indigo-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download to View
              </button>
            </div>
          );
        }

      case 'spreadsheet':
        return (
          <div className="flex flex-col items-center justify-center p-12 bg-green-50 rounded-lg">
            <File className="w-16 h-16 text-green-500 mb-4" />
            <p className="text-gray-700 font-medium mb-2">
              Spreadsheet Document
            </p>
            <p className="text-sm text-gray-500 mb-6">{document.name}</p>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Open in Excel/Sheets
            </button>
          </div>
        );

      default:
        return (
          <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg">
            <File className="w-16 h-16 text-gray-400 mb-4" />
            <p className="text-gray-600 mb-2">Preview Not Available</p>
            <p className="text-sm text-gray-500 mb-6">
              File type: .{document.fileType}
            </p>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download File
            </button>
          </div>
        );
    }
  };

  const fileTypeInfo = getFileTypeInfo(document.fileType);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {fileTypeInfo.icon}
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {document.name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Type: {getFileTypeDisplayName(document.fileType)} • Status:{' '}
                  {document.status} •
                  {document.useAsExample && (
                    <span className="text-yellow-600 font-medium ml-1">
                      Example Document
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* <button
                onClick={handleOpenInNewTab}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="w-5 h-5" />
              </button> */}
              <button
                onClick={onClose}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">{renderPreview()}</div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <p>Created: {new Date(document.createdAt).toLocaleString()}</p>
              <p>Updated: {new Date(document.updatedAt).toLocaleString()}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-brand-indigo-600 text-white rounded-lg hover:bg-brand-indigo-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}